import { useId, useRef, useState } from 'react';
import type { Attachment } from '@shared/types';
import { api } from '../lib/api';
import { compressImage } from '../lib/images';
import { useToast } from '../lib/toast';
import { fileSize } from '../lib/format';

/** Read-only grid of files already attached to a record. */
export function AttachmentGrid({ items }: { items: Attachment[] }) {
  if (items.length === 0) return null;

  const images = items.filter((item) => item.contentType.startsWith('image/'));
  const docs = items.filter((item) => !item.contentType.startsWith('image/'));

  return (
    <div className="photos">
      {images.map((item) => (
        <a key={item.id} href={item.url} target="_blank" rel="noreferrer" title={item.filename}>
          <img src={item.url} alt={item.filename} loading="lazy" />
        </a>
      ))}
      {docs.map((item) => (
        <a key={item.id} className="doc" href={item.url} target="_blank" rel="noreferrer">
          <span aria-hidden="true">📄</span>
          <span>{item.filename}</span>
        </a>
      ))}
    </div>
  );
}

interface UploaderProps {
  value: Attachment[];
  onChange: (attachments: Attachment[]) => void;
  accept?: string;
  label?: string;
  /** Offer the dedicated "Take photo" button. */
  camera?: boolean;
  max?: number;
  disabled?: boolean;
}

/**
 * Uploads immediately and hands back attachment ids. Parent records claim
 * those ids on save, so a half-finished form never blocks the upload.
 */
export function AttachmentUploader({
  value,
  onChange,
  accept = 'image/*,application/pdf',
  label = 'Add photos or documents',
  camera = true,
  max = 10,
  disabled = false,
}: UploaderProps) {
  const { fail, notify } = useToast();
  const [busy, setBusy] = useState(false);
  const cameraInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const describedBy = useId();

  async function handleFiles(list: FileList | null) {
    if (!list || list.length === 0) return;

    const remaining = max - value.length;
    if (remaining <= 0) {
      notify(`You can attach up to ${max} files.`, 'error');
      return;
    }

    setBusy(true);
    try {
      const chosen = Array.from(list).slice(0, remaining);
      const prepared = await Promise.all(chosen.map(compressImage));
      const { attachments } = await api.upload(prepared);
      onChange([...value, ...attachments]);
    } catch (error) {
      fail(error, 'That upload did not go through.');
    } finally {
      setBusy(false);
      if (cameraInput.current) cameraInput.current.value = '';
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function remove(attachment: Attachment) {
    onChange(value.filter((item) => item.id !== attachment.id));
    await api.deleteUpload(attachment.id).catch(() => undefined);
  }

  return (
    <div>
      <div className="row" style={{ marginTop: 8 }}>
        {camera && (
          <label className="picker">
            <span aria-hidden="true">📷</span> Take photo
            <input
              ref={cameraInput}
              type="file"
              accept="image/*"
              capture="environment"
              disabled={disabled || busy}
              onChange={(event) => void handleFiles(event.target.files)}
            />
          </label>
        )}
        <label className="picker">
          <span aria-hidden="true">＋</span> {label}
          <input
            ref={fileInput}
            type="file"
            accept={accept}
            multiple
            disabled={disabled || busy}
            aria-describedby={describedBy}
            onChange={(event) => void handleFiles(event.target.files)}
          />
        </label>
        {busy && <span className="spinner" aria-label="Uploading" />}
      </div>

      <div id={describedBy} className="mut" style={{ marginTop: 6 }}>
        Up to {max} files, 12 MB each. Photos are resized automatically.
      </div>

      {value.length > 0 && (
        <div className="photos">
          {value.map((item) => (
            <div key={item.id} className="photo-pending">
              {item.contentType.startsWith('image/') ? (
                <img src={item.url} alt={item.filename} />
              ) : (
                <span className="doc">
                  <span aria-hidden="true">📄</span>
                  <span>
                    {item.filename} · {fileSize(item.size)}
                  </span>
                </span>
              )}
              <button
                type="button"
                className="remove"
                aria-label={`Remove ${item.filename}`}
                onClick={() => void remove(item)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
